import type { ChainFamily } from './chains';
import type { UseWalletResult, UseWalletUIResult } from './types';

import { useCallback } from 'react';

import { DEFAULT_FAMILY } from './registry';
import { useEvmWalletHook, useTronWalletHook, useEvmWalletUIHook, useTronWalletUIHook } from './sdk.client';

/**
 * Wallet of one family (multichain spec §4.6). Screens pass the family of the chain they are
 * working on; the ones that predate the multichain track pass nothing and keep talking to
 * `DEFAULT_FAMILY` — TRON — exactly as before.
 *
 * Both families are read on every render and the result is picked afterwards, rather than calling
 * one hook or the other: the family is a runtime value, and hook order may not depend on it. The
 * `__…_ENABLED__` guards are build constants, so they do not move hook order either — they are
 * what keeps the wallet stack of a family this build does not carry out of the bundle, and what
 * stops a call into a wallet context that was never mounted.
 *
 * The static imports above are fine where the lazy ones in provider.tsx are not: these modules
 * only re-export hooks (no module-scope side effect), so the bundler drops them together with the
 * folded-away branch. It is `evm/config.ts` — reached from EvmProvider, never from here — that
 * builds a wagmi config at import time.
 *
 * On the server no wallet context is mounted and no wallet SDK is loaded (`sdk.client.ts`): there a
 * wallet is not connected, whatever the build carries. `ON_CLIENT` is a literal like the two above.
 */

const ON_CLIENT = !import.meta.env.SSR;

const DISCONNECTED: UseWalletResult = {
  wallet: null,
  isConnecting: false,
  isConnected: false,
};

/** UI of a family this build does not carry: never silently a no-op, always a named failure. */
function unavailableWalletUI(family: ChainFamily): UseWalletUIResult {
  const fail = async (): Promise<never> => {
    throw new Error(`Wallet family "${family}" is not enabled in this build`);
  };

  return {
    signMessage: fail,
    sendTransaction: fail,
    transferToken: fail,
    callContract: fail,
    readContract: fail,
    disconnect: async () => {},
  };
}

// Built once: consumers keep these in effect/callback dependencies, and a fresh object per render
// would retrigger them forever.
const TRON_UNAVAILABLE = unavailableWalletUI('tron');
const EVM_UNAVAILABLE = unavailableWalletUI('evm');

export function useWallet(family: ChainFamily = DEFAULT_FAMILY): UseWalletResult {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const tron = __TRON_ENABLED__ && ON_CLIENT ? useTronWalletHook() : DISCONNECTED;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const evm = __EVM_ENABLED__ && ON_CLIENT ? useEvmWalletHook() : DISCONNECTED;

  return family === 'evm' ? evm : tron;
}

export function useWalletUI(family: ChainFamily = DEFAULT_FAMILY): UseWalletUIResult {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const tron = __TRON_ENABLED__ && ON_CLIENT ? useTronWalletUIHook() : TRON_UNAVAILABLE;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const evm = __EVM_ENABLED__ && ON_CLIENT ? useEvmWalletUIHook() : EVM_UNAVAILABLE;

  return family === 'evm' ? evm : tron;
}

/**
 * Disconnects the wallet of every family this build carries (app#183, Sign out).
 *
 * Lives here, next to the family set, rather than in the screen that needs it: which families
 * exist and how each one disconnects is exactly what this module already knows, and a screen
 * naming `'tron'` and `'evm'` itself would be the first one to do so. Best-effort per family —
 * an adapter with nothing connected may refuse, and a family that is not in this build answers
 * with the no-op above — and the caller decides how long it is willing to wait.
 */
export function useDisconnectWallets(): () => Promise<void> {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const tron = __TRON_ENABLED__ && ON_CLIENT ? useTronWalletUIHook() : TRON_UNAVAILABLE;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const evm = __EVM_ENABLED__ && ON_CLIENT ? useEvmWalletUIHook() : EVM_UNAVAILABLE;
  const disconnectTron = tron.disconnect;
  const disconnectEvm = evm.disconnect;

  return useCallback(async () => {
    await Promise.allSettled([disconnectTron(), disconnectEvm()]);
  }, [disconnectTron, disconnectEvm]);
}
